import type {
  AvailabilityCalendar,
  AvailabilityCustomBlock,
  AvailabilityKind,
  AvailabilityRecurringBlock,
} from './types'
import { EMPTY_AVAILABILITY_CALENDAR } from './types'

function parseSettingsRoot(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  return raw as Record<string, unknown>
}

function isAvailabilityKind(value: unknown): value is AvailabilityKind {
  return value === 'available' || value === 'unavailable'
}

function parseRecurringBlock(raw: unknown): AvailabilityRecurringBlock | null {
  if (!raw || typeof raw !== 'object') return null
  const b = raw as Record<string, unknown>
  if (typeof b.id !== 'string') return null
  if (!isAvailabilityKind(b.kind)) return null
  const dayOfWeekStart = typeof b.dayOfWeekStart === 'number' ? b.dayOfWeekStart : b.dayOfWeek
  const dayOfWeekEnd = typeof b.dayOfWeekEnd === 'number' ? b.dayOfWeekEnd : b.dayOfWeek
  if (typeof dayOfWeekStart !== 'number' || typeof dayOfWeekEnd !== 'number') return null
  if (
    typeof b.startHour !== 'number' ||
    typeof b.startMinute !== 'number' ||
    typeof b.endHour !== 'number' ||
    typeof b.endMinute !== 'number'
  ) {
    return null
  }
  return {
    id: b.id,
    kind: b.kind,
    dayOfWeekStart,
    startHour: b.startHour,
    startMinute: b.startMinute,
    dayOfWeekEnd,
    endHour: b.endHour,
    endMinute: b.endMinute,
    notes: typeof b.notes === 'string' ? b.notes : undefined,
  }
}

function parseCustomBlock(raw: unknown): AvailabilityCustomBlock | null {
  if (!raw || typeof raw !== 'object') return null
  const b = raw as Record<string, unknown>
  if (typeof b.id !== 'string') return null
  if (!isAvailabilityKind(b.kind)) return null
  if (typeof b.startTime !== 'string' || typeof b.endTime !== 'string') return null
  const startMs = Date.parse(b.startTime)
  const endMs = Date.parse(b.endTime)
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null
  return {
    id: b.id,
    kind: b.kind,
    startTime: b.startTime,
    endTime: b.endTime,
    notes: typeof b.notes === 'string' ? b.notes : undefined,
  }
}

export function parseAvailabilityCalendar(raw: unknown): AvailabilityCalendar | null {
  if (!raw || typeof raw !== 'object') return null
  const c = raw as Record<string, unknown>
  const recurringBlocks = Array.isArray(c.recurringBlocks)
    ? c.recurringBlocks.map(parseRecurringBlock).filter((b): b is AvailabilityRecurringBlock => b != null)
    : []
  const customBlocks = Array.isArray(c.customBlocks)
    ? c.customBlocks.map(parseCustomBlock).filter((b): b is AvailabilityCustomBlock => b != null)
    : []
  return {
    recurringBlocks,
    customBlocks,
    updatedAt: typeof c.updatedAt === 'string' ? c.updatedAt : new Date().toISOString(),
  }
}

export function getAvailabilityCalendarFromSettings(settingsJson: unknown): AvailabilityCalendar {
  const root = parseSettingsRoot(settingsJson)
  return parseAvailabilityCalendar(root.availabilityCalendar) ?? EMPTY_AVAILABILITY_CALENDAR()
}
